import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { VaAccordion } from '@department-of-veterans-affairs/component-library/dist/react-bindings';

import { scrollTo } from 'platform/utilities/scroll';
import { focusElement } from 'platform/utilities/ui';
import { SCROLL_ELEMENT_SUFFIX } from '../../../../utilities/constants';
import ReviewCollapsibleChapter from './ReviewCollapsibleChapter';
import ReviewFlatChapter from './ReviewFlatChapter';
import {
  FLAT_REVIEW_SUCCESS_MESSAGE,
  flatReviewSectionId,
  isFlatReviewEnabled,
} from './flatReview';
import {
  consumeReviewEditChangedChapters,
  consumeReviewEditReturnFocus,
  consumeReviewEditSuccessAlert,
  finishReviewEditTrip,
  rememberReviewEditPages,
  takeReviewEditBaseline,
} from './reviewEditTrip';
import {
  createPageListByChapter,
  getActiveExpandedPages,
  getActiveChapters,
  getPageKeys,
} from '../helpers';
import { getReviewPageOpenChapters, getViewedPages } from '../state/selectors';
import {
  closeReviewChapter,
  openReviewChapter,
  toggleAllReviewChapters,
  setData,
  setEditMode,
  setViewedPages,
  uploadFile,
} from '../actions';

import { getPageKey } from '../utilities/review';

class ReviewChapters extends React.Component {
  constructor(props) {
    super(props);
    const successFromReturn = props.location?.state?.reviewEditSuccess
      ? FLAT_REVIEW_SUCCESS_MESSAGE
      : '';
    this.state = {
      successAlert: successFromReturn,
      successPageKey: successFromReturn
        ? props.location?.state?.reviewEditReturnPageKey || ''
        : '',
      changedChapters: [],
    };
  }

  componentDidMount() {
    const { formData, pageList, location } = this.props;
    const viewedPages = new Set(getPageKeys(pageList, formData));
    this.props.setViewedPages(viewedPages);
    if (isFlatReviewEnabled(this.props.formConfig, this.props.featureToggles)) {
      // Browser back leaves the trip active. Finish it with no cancel/update
      // event so the next Edit can fire trip-start and stash a fresh baseline.
      if (this.props.form?.reviewEditTrip?.active) {
        takeReviewEditBaseline(
          this.props.form?.formId || this.props.formConfig?.formId,
        );
        this.props.finishReviewEditTrip?.();
      }
      this.props.rememberReviewEditPages?.([...viewedPages]);
      const storedSuccess = consumeReviewEditSuccessAlert();
      const successAlert =
        this.state.successAlert ||
        (storedSuccess ? FLAT_REVIEW_SUCCESS_MESSAGE : '');
      const pageKey =
        location?.state?.reviewEditReturnPageKey ||
        this.state.successPageKey ||
        consumeReviewEditReturnFocus();
      if (location?.state?.reviewEditReturnPageKey) {
        consumeReviewEditReturnFocus();
      }
      const changedFromReturn = location?.state?.reviewEditChangedChapters;
      this.setState(
        {
          successAlert,
          successPageKey: successAlert ? pageKey : '',
          changedChapters: Array.isArray(changedFromReturn)
            ? changedFromReturn
            : consumeReviewEditChangedChapters(),
        },
        () => {
          requestAnimationFrame(() => this.focusReturnedSection(pageKey));
        },
      );
      return;
    }
    this.focusReturnedSection();
  }

  focusReturnedSection = pageKey => {
    if (
      !isFlatReviewEnabled(this.props.formConfig, this.props.featureToggles)
    ) {
      return;
    }

    const focusKey = pageKey || consumeReviewEditReturnFocus();
    if (!focusKey) {
      return;
    }

    const alert = document.getElementById('flat-review-success-alert');
    const section = document.querySelector(
      `[data-flat-review-page="${focusKey}"]`,
    );
    const heading =
      document.getElementById(flatReviewSectionId(focusKey)) ||
      section?.querySelector('h2, h3');
    const target = alert || heading || section;

    if (target) {
      scrollTo(`${focusKey}${SCROLL_ELEMENT_SUFFIX}`);
      focusElement(target);
    }
  };

  handleToggleChapter = ({ target }) => {
    if (target) {
      const name = target.dataset?.chapter;
      const isOpen = target.getAttribute('open');
      if (isOpen === 'true') {
        this.props.openReviewChapter(name);
        scrollTo(`chapter${name}ScrollElement`);
      } else {
        const chapter = this.props.chapters.find(chapt => chapt.name === name);
        this.props.closeReviewChapter(name, chapter?.pageKeys);
      }
    }
  };

  handleToggleAllChapters = ({ detail }) => {
    const { status } = detail;
    const allOpen = status === 'allOpen';
    const chapterNames = this.props.chapters.reduce((acc, chapter) => {
      acc[chapter.name] = allOpen;
      return acc;
    }, {});
    this.props.toggleAllReviewChapters(chapterNames);
  };

  handleEdit = (pageKey, editing, index = null) => {
    const fullPageKey = `${pageKey}${index ?? ''}`;
    if (editing) {
      this.props.setViewedPages([fullPageKey]);
    }
    this.props.setEditMode(pageKey, editing, index);
  };

  handleSetData = (...args) => {
    this.props.setData(...args);
    if (this.props.onSetData) {
      this.props.onSetData();
    }
  };

  render() {
    const {
      chapters,
      form,
      formConfig,
      formContext,
      viewedPages,
      pageList,
      featureToggles,
    } = this.props;

    const chapterProps = chapter => ({
      expandedPages: chapter.expandedPages,
      chapterFormConfig: chapter.formConfig,
      chapterKey: chapter.name,
      form,
      reviewErrors: formConfig?.reviewErrors,
      formContext,
      onEdit: this.handleEdit,
      pageKeys: chapter.pageKeys,
      pageList,
      reviewEditFocusOnHeaders: formConfig?.reviewEditFocusOnHeaders ?? true,
      setData: (...args) => this.handleSetData(...args),
      hasUnviewedPages: chapter.hasUnviewedPages,
      uploadFile: this.props.uploadFile,
      viewedPages,
      formOptions: formConfig?.formOptions,
      urlPrefix: formConfig?.urlPrefix || '',
      changedChapters: this.state.changedChapters,
      successAlert: this.state.successAlert,
      successPageKey: this.state.successPageKey,
    });

    if (isFlatReviewEnabled(formConfig, featureToggles)) {
      return (
        <article className="schemaform-flat-review">
          <p>
            Please review the information below. If anything needs to be
            corrected, select the Edit link in that section.
          </p>
          <va-on-this-page />
          {chapters.map(chapter => (
            <ReviewFlatChapter key={chapter.name} {...chapterProps(chapter)} />
          ))}
        </article>
      );
    }

    return (
      <VaAccordion
        bordered
        onAccordionExpandCollapseAll={this.handleToggleAllChapters}
        onAccordionItemToggled={this.handleToggleChapter}
        uswds
        data-dd-privacy="mask"
        data-dd-action-name="review chapters"
      >
        {chapters.map(chapter => (
          <ReviewCollapsibleChapter
            key={chapter.name}
            {...chapterProps(chapter)}
            open={chapter.open}
          />
        ))}
      </VaAccordion>
    );
  }
}

export function mapStateToProps(state, ownProps) {
  // from ownprops
  const { formConfig, formContext, pageList } = ownProps;

  // from redux state
  const { form } = state;
  const formData = form.data;
  const openChapters = getReviewPageOpenChapters(state) || {};
  const viewedPages = getViewedPages(state);

  const chapterNames = getActiveChapters(formConfig, formData);
  const pagesByChapter = createPageListByChapter(formConfig);
  const chapters = chapterNames.map(chapterName => {
    const pages = pagesByChapter[chapterName];

    const expandedPages = getActiveExpandedPages(pages, formData);
    const chapterFormConfig = formConfig.chapters[chapterName];
    const open = openChapters[chapterName] || false;
    const pageKeys = getPageKeys(pages, formData);

    const hasErrors = form.formErrors?.errors?.some(err =>
      pageKeys.includes(getPageKey(err)),
    );
    const hasUnviewedPages =
      hasErrors || pageKeys.some(key => !viewedPages.has(key));

    return {
      expandedPages: expandedPages.map(page =>
        page.appStateSelector
          ? { ...page, appStateData: page.appStateSelector(state) }
          : page,
      ),
      formConfig: chapterFormConfig,
      name: chapterName,
      open,
      pageKeys,
      hasUnviewedPages,
    };
  });

  return {
    chapters,
    form,
    formData,
    formConfig,
    formContext,
    pageList,
    viewedPages,
    featureToggles: state.featureToggles || {},
  };
}

const mapDispatchToProps = {
  closeReviewChapter,
  openReviewChapter,
  toggleAllReviewChapters,
  setData,
  setEditMode,
  setViewedPages,
  uploadFile,
  rememberReviewEditPages,
  finishReviewEditTrip,
};

ReviewChapters.propTypes = {
  chapters: PropTypes.array.isRequired,
  closeReviewChapter: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  formConfig: PropTypes.object.isRequired,
  formData: PropTypes.object.isRequired,
  openReviewChapter: PropTypes.func.isRequired,
  pageList: PropTypes.array.isRequired,
  setData: PropTypes.func.isRequired,
  setEditMode: PropTypes.func.isRequired,
  setViewedPages: PropTypes.func.isRequired,
  toggleAllReviewChapters: PropTypes.func.isRequired,
  uploadFile: PropTypes.func.isRequired,
  viewedPages: PropTypes.object.isRequired,
  featureToggles: PropTypes.object,
  formContext: PropTypes.object,
  location: PropTypes.shape({
    state: PropTypes.shape({
      reviewEditReturnPageKey: PropTypes.string,
    }),
  }),
  finishReviewEditTrip: PropTypes.func,
  onSetData: PropTypes.func,
  rememberReviewEditPages: PropTypes.func,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ReviewChapters),
);

// for tests
export { ReviewChapters };
